import { useEffect, useId, useRef, type ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface ModalProps {
  eyebrow?: string;
  title: string;
  open: boolean;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  /** 追加在 `.modal` 上的类名（如 `.modal-wide`），用于需要更宽版式的详情类对话框。 */
  className?: string;
}

/**
 * 原型基元：`.modal-backdrop` / `.modal` / `.modal-content` / `.modal-footer`。
 * 关闭态沿用原型的 `hidden` 属性，交互（Esc 关闭、焦点回归）按 specs/07 §8 补齐。
 */
export function Modal({ eyebrow, title, open, onClose, footer, children, className }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  // 最新 onClose 的引用。Esc 回调从这里读，避免把 onClose 写进下方 effect 的依赖：
  // 调用方几乎都传内联箭头函数，每次父级重渲染都会换新引用；若 effect 依赖它，
  // 输入框每敲一个字（setState → 重渲染）就会重跑 effect、重新聚焦对话框容器，
  // 把焦点从输入框抢走，表现为「中文/连续输入只能进一个字符」。
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={cn("modal", className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header>
          <div>
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button className="icon-button" type="button" title="关闭" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="modal-content">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </section>
    </div>
  );
}
